import { Text, Group, CloseButton } from "@mantine/core"
import { IconPlus } from "@tabler/icons-react"
import { Button, InputWithMenu } from "@tfg/ui"

import { ProductSheetLanguages } from "@tfg/types/constants/language.constants"

import classes from "~/assets/css/Global.module.css"

export type LanguageItem = {
  label: string
  value: ProductSheetLanguages
  icon: JSX.Element
  deletable: boolean
}

type Props = {
  selectedLocales: ProductSheetLanguages[]
  languageItems: LanguageItem[]
  onChange: (selected: ProductSheetLanguages[]) => void
  width: string | number
  isSelectButtonDisabled?: boolean
  limitReached?: boolean
}

export const LanguageMultiSelect = ({
  languageItems,
  selectedLocales,
  onChange,
  width,
  isSelectButtonDisabled = false,
  limitReached = false,
}: Props) => {
  const renderSelected = () => {
    if (selectedLocales.length === 0) {
      return
    }
    return (
      <Group gap="sm">
        {selectedLocales.map((locale) => {
          const lang = languageItems.find((li) => li.value === locale)
          if (!lang) return null
          return (
            <Group
              key={lang.value}
              gap={4}
              className={classes.pill}
              style={{
                userSelect: "none",
              }}
            >
              {lang.icon}
              <Text size="sm" fw="bold">
                {lang.label.replace(/\s*\(default\)$/, "")}
              </Text>
              {lang.deletable && (
                <CloseButton
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    onChange(
                      selectedLocales.filter(
                        (val: string) => val !== lang.value
                      )
                    )
                  }}
                  size="xs"
                  iconSize={14}
                  ml={4}
                />
              )}
            </Group>
          )
        })}
      </Group>
    )
  }

  const handleClick = (languageItem: LanguageItem) => {
    const isSelected = selectedLocales.includes(languageItem.value)
    if (isSelected || limitReached) {
      return
    } else {
      onChange([...selectedLocales, languageItem.value])
    }
  }

  return (
    <InputWithMenu
      menuWidth={width}
      rightSectionIcon={
        <Button
          variant="primary"
          p={0}
          miw={0}
          h={24}
          w={24}
          disabled={isSelectButtonDisabled}
        >
          <IconPlus stroke={2} />
        </Button>
      }
      items={languageItems
        .map((languageItem) => ({
          label: languageItem.label,
          icon: languageItem.icon,
          onClick: () => handleClick(languageItem),
          disabled: !languageItem.deletable || limitReached,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))}
    >
      {renderSelected()}
    </InputWithMenu>
  )
}
